import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { validationService } from "../services/validation.service";
import { getVerificationResultLabel } from "../utils/verificationLabels";
import styles from "../styles/pages/EmailVerification.module.css";

const POLL_INTERVAL = 7000;

const RESULT_CHIP_CLASSES = {
  deliverable: "chipDeliverable",
  risky: "chipRisky",
  undeliverable: "chipUndeliverable",
  unknown: "chipUnknown",
};

function ResultChip({ result }) {
  const chipClass = styles[RESULT_CHIP_CLASSES[result]] || styles.chipUnknown;
  return (
    <span className={`${styles.resultChip} ${chipClass}`}>
      {getVerificationResultLabel(result)}
    </span>
  );
}

function formatTaskDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function EmailVerificationPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { activeAccountId } = useAuth();

  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previousTasks, setPreviousTasks] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setTaskData(null);

    async function fetchTask() {
      try {
        const data = await validationService.getVerificationTask(taskId);
        setTaskData(data);
        if (data.status === "finished") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchTask();
    pollRef.current = setInterval(fetchTask, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [taskId]);

  useEffect(() => {
    if (!activeAccountId) return;
    validationService
      .listVerificationTasks(activeAccountId)
      .then(setPreviousTasks)
      .catch(() => {});
  }, [activeAccountId, taskId]);

  const isFinished = taskData?.status === "finished";
  const rows = taskData?.rows ?? [];

  const summaryChips = taskData
    ? [
        { result: "deliverable", count: taskData.deliverable_count ?? 0 },
        { result: "risky", count: taskData.risky_count ?? 0 },
        { result: "undeliverable", count: taskData.undeliverable_count ?? 0 },
        { result: "unknown", count: taskData.unknown_count ?? 0 },
      ]
    : [];

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.topBar}>
            <button
              className={styles.backBtn}
              onClick={() => navigate("/database")}
              type="button"
            >
              ← Volver a Database
            </button>
            <div className={styles.pageTitle}>Verificación de emails</div>
          </div>

          {loading && (
            <div className={styles.statusCard}>
              <div className={styles.spinner} />
              <span className={styles.statusText}>Cargando...</span>
            </div>
          )}

          {!loading && !isFinished && (
            <div className={styles.statusCard}>
              <div className={styles.spinner} />
              <div className={styles.statusBody}>
                <div className={styles.statusText}>Verificando emails...</div>
                <div className={styles.statusSub}>
                  {taskData?.total ?? "?"} emails enviados a verificar. Te vamos
                  a notificar cuando termine.
                </div>
              </div>
            </div>
          )}

          {!loading && isFinished && (
            <>
              <div className={styles.summaryChips}>
                {summaryChips.map(({ result, count }) => (
                  <div key={result} className={styles.summaryChip}>
                    <ResultChip result={result} />
                    <span className={styles.summaryCount}>{count}</span>
                  </div>
                ))}
              </div>

              <div className={styles.tableWrap}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>Email</div>
                  <div className={styles.headerCellCenter}>Resultado</div>
                  <div className={styles.headerCellCenter}>Score</div>
                </div>

                {rows.map((row) => (
                  <div key={row.id} className={styles.tableRow}>
                    <div className={styles.cell}>{row.email ?? "-"}</div>
                    <div className={styles.cellCenter}>
                      <ResultChip result={row.result} />
                    </div>
                    <div className={styles.cellCenter}>{row.score ?? "-"}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {previousTasks.length > 0 && (
            <div className={styles.historySection}>
              <div className={styles.historyTitle}>
                Verificaciones anteriores
              </div>
              <div className={styles.historyList}>
                {previousTasks.map((task) => {
                  const isCurrent = String(task.id) === String(taskId);
                  return (
                    <button
                      key={task.id}
                      type="button"
                      className={`${styles.historyRow} ${
                        isCurrent ? styles.historyRowCurrent : ""
                      }`}
                      onClick={() =>
                        !isCurrent &&
                        navigate(`/database/verification/${task.id}`)
                      }
                    >
                      <span className={styles.historyDate}>
                        {formatTaskDate(task.created_at)}
                      </span>
                      <span className={styles.historyTotal}>
                        {task.total} emails
                      </span>
                      {task.status === "finished" ? (
                        <span className={styles.historyCounts}>
                          <span className={styles.historyCountDeliverable}>
                            {task.deliverable_count ?? 0} entregables
                          </span>
                          <span className={styles.historyCountRisky}>
                            {task.risky_count ?? 0} riesgosos
                          </span>
                          <span className={styles.historyCountUndeliverable}>
                            {task.undeliverable_count ?? 0} no entregables
                          </span>
                        </span>
                      ) : (
                        <span className={styles.historyRunning}>
                          En proceso…
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
